import { useCallback, useEffect, useMemo, useState } from "react";
import { IMenuOption } from "../../../types/global";
import { useTranslation } from "react-i18next";
import { useStateContext } from "../../../contexts/auth-context";

export const useQuotationOption = (status?: string | null) => {
  const {
    state: { permissions },
  } = useStateContext();

  const defaultOptions: IMenuOption[] = useMemo(
    () => [
      {
        value: "แก้ไข",
        disabled: false,
      },
      {
        value: "คัดลอก",
        disabled: false,
      },
      {
        value: "ยกเลิก",
        disabled: false,
      },
    ],
    []
  );

  const editOption = defaultOptions[0];
  const copyOption = defaultOptions[1];
  const cancelOption = defaultOptions[2];

  const canUpdateAndCancel = useCallback(() => {
    return permissions?.quotation?.update && permissions?.quotation?.cancel;
  }, [permissions]);

  const getOptionsForStatus = useCallback(
    (status?: string | null) => {
      const isCreate = permissions?.quotation?.create;

      if (["draft", "not_approved"].includes(status || "")) {
        if (canUpdateAndCancel()) {
          return isCreate ? [copyOption, cancelOption] : [cancelOption];
        }
        return isCreate ? [copyOption] : [];
      }

      if (status === "cancelled") {
        return isCreate ? [copyOption] : [];
      }

      if (status === "approved") {
        if (canUpdateAndCancel()) {
          return isCreate ? [...defaultOptions] : [editOption, cancelOption];
        }
        return isCreate ? [copyOption] : [];
      }

      if (status === "finished") {
        if (canUpdateAndCancel()) {
          return isCreate ? [copyOption] : [];
        }
        return isCreate ? [copyOption] : [];
      }

      // Default case when no specific status matches
      return canUpdateAndCancel()
        ? isCreate
          ? defaultOptions
          : [editOption, cancelOption]
        : isCreate
        ? [copyOption]
        : [];
    },
    [
      canUpdateAndCancel,
      copyOption,
      cancelOption,
      editOption,
      defaultOptions,
      permissions,
    ]
  );

  const [selectModifyOptions, setSelectedModifyOptions] =
    useState<IMenuOption[]>(defaultOptions);

  useEffect(() => {
    const options = getOptionsForStatus(status);
    setSelectedModifyOptions(options);
  }, [getOptionsForStatus, status]);

  return selectModifyOptions;
};

export const useQuotationCreate = (status?: string | null) => {
  const { t } = useTranslation();
  const {
    state: { permissions },
  } = useStateContext();

  const defaultOptions: IMenuOption[] = useMemo(
    () => [
      ...(permissions?.sales_order?.create
        ? [
            {
              value: t("sales.order.index"),
              disabled: false,
            },
          ]
        : []),
    ],
    [t, permissions?.sales_order?.create]
  );

  const [disabled, setDisabled] = useState<boolean>(false);
  useEffect(() => {
    switch (status) {
      case "accepted":
      case "finished":
        setDisabled(false);
        break;
      default:
        setDisabled(true);
    }
  }, [status]);
  return {
    selectCreateOptions: defaultOptions,
    disabledCreateOptions: disabled,
  };
};
